import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import __ from "lodash";
import parse from 'html-react-parser';
import axios from "axios";
import {Checkbox, Icon, IconButton, Tooltip, useTheme} from "@material-ui/core";
import MaterialTable from "@material-table/core";
import {defaultComponentsScroll, MaterialTableBody, FullWidthFilter} from "../../../controls/Material-Table-Components";
import SystemContext from "../../../SystemContext";
import {formatChars} from "../../../blocks/FormatCharacters";
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import useDimensions from "../../../blocks/useDimensions";
import {ColumnWidth, renderBooleanFilter} from "../../../blocks/ColumnHelper";
import {CheckboxEditor, MaskedText} from "../../../controls";
import { useMenuItems, useAddMenuItem, useUpdateMenuItem }  from "../../../services/settings";

const apiSitePrefix = getApiSitePrefix();
const dateFormat = "DD/MM/YYYY";


const MenuRootList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;

  const props = {
    ..._props
  }
  const classes = props.classes;
  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddMenuItem = useAddMenuItem();
  const handleUpdateMenuItem = useUpdateMenuItem();

  const queryClient = useQueryClient();
  const extraKeys = ["root"];

  const rootMenuItems = useMenuItems({tableRef, queryClient, extraKeys});

  const handleRowClick = (event, rowData) => {
    if (rowData.tableData.index >= 0) {
      if (eventBus) {
        eventBus.trigger('MenuItemList.rowSelected', rowData);
      }
    }
  }

  const handleDuplicate = (rowData) => {
    let message = parse(`Are you sure you wish to duplicate ${rowData.tariffType === 'DEFAULT' ? `the <strong>${rowData.description.replace("Tariff", "")}</strong> tariff`: `<strong>${rowData.companyName}'s</strong> tariff`}?`);
    SystemData.confirm(
      "Duplicate Menu",
      message,
      [
        {icon: "check_circle", value: true, label: "Duplicate", options: {color:"primary"}},
        {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
      ],
      async (response) => {
        if (response) {
          try {
            SystemData.toggleLoadingProgress(true, `Duplicating Root Menu ...`);
            const result = await axios.put(`${apiSitePrefix}/menuitems/duplicate`, {rowData});
            SystemData.toggleLoadingProgress(false);
            if (result && __.has(result, "data")) {
              const data = result.data;
              if (data.errors && Array.isArray(data.errors)) {
                console.log(result);
                const title = `${data.success ? `Root menu duplicated with some Errors` : `Failed to duplicate root menu`}`
                SystemData.alert(title, data.errors.join(", "));
              }
            }
          } catch (error) {
            SystemData.toggleLoadingProgress(false);
            const title = `Failed to duplicate root menu`
            SystemData.alert(title, `Failed to duplicate root menu with: ${error}`);
          }
          queryClient.invalidateQueries("MenuItems");
        }
      }
    );
  }

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize:13, padding: "0px 0px" },
      headerStyle: { padding: "8px 2px" },
      filterCellStyle: { padding: "8px 2px" },
      editable: state.canEdit?"always":"never",
      filterComponent: FullWidthFilter,
      sorting: false,
    };

    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...ColumnWidth(220),
        title: "Root Menu Name",
        field: "name",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(220),
        title: "ID",
        field: "id",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(50),
        title: "Active",
        field: "tariffActive",
        defaultFilter: undefined, // 'checked', 'unchecked', or undefined
        filterComponent: (props) => <div style={{marginLeft: 0}}>{renderBooleanFilter({...props, size: "medium", textAlign: "center"})}</div>,
        editComponent: (props) => (
          <div style={{textAlign: "center"}}>
            <CheckboxEditor
              value={props.value}
              onChange={ (checked) => {
                const data = {
                  ...props.rowData,
                  tariffActive: checked,
                }
                props.onRowDataChange(data);
              }}
            />
          </div>
        ),
        render: rowData => <div style={{textAlign: "center"}}><Checkbox style={{paddingLeft: 10}} checked={rowData.tariffActive === true} value="true" /></div>
      },
      {
        ...baseColumn,
        ...ColumnWidth(75),
        title: "Action",
        field: "tariffActive",
        filtering: false,
        editable: "never",
        readonly: true,
        render: (rowData)=> {
          console.log(tableRef.current.state.highlightedRow);
          return(
            <div style={{textAlign: "right"}}>
              <Tooltip title={"Duplicate Menu"}>
                <IconButton
                  color={state.canEdit ? "secondary" : "#979797"}
                  aria-label="duplicate menu"
                  onClick={()=>handleDuplicate(rowData)}
                  disabled={!state.canEdit}
                >
                  <Icon disabled={!state.canEdit}>file_copy</Icon>
                </IconButton>
              </Tooltip>
            </div>
          )
        },
      }
    ];
  };

  const [tableContainerRef, {height: tableHeight}] = useDimensions({});
  const theader = tableContainerRef.current?.querySelector('thead');
  const thead = theader ? theader.clientHeight:40;
  const bodyHeight = tableHeight - thead - 16;

  return (isAuthenticated &&
      <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
        <div style={{background: "#ffffff", width: "calc(100% - 24px)", paddingTop: 16, paddingLeft: 20, paddingRight: 4}}>
          <div ref={tableContainerRef} className={state.canEdit?classes.tariffHeaderListEdit:classes.tariffHeaderList} style={{width: "100%", minWidth: "100%", height: `calc(100vh - ${props.height + 16}px)`}}>
            <MaterialTable
              icons={{ Filter: React.forwardRef((props, ref) => <Icon className="filterIcon">filter_list</Icon> )}}
              tableRef={tableRef}
              style={{
                border: "none",
                boxShadow: "none",
                borderRadius: 0,
              }}
              onRowClick={handleRowClick}
              options={{
                tableRef,
                highlightRowStyle: {backgroundColor: "#F69259"},
                actionsColumnIndex: -99,
                cellStyle: {
                  padding: "8px 2px",
                },
                headerRowStyle: {
                  display: "table",
                  width: "100%",
                },
                headerStyle: {
                  backgroundColor: theme.palette.tableHeader.backgroundColor,
                  color: theme.palette.tableHeader.textColor,
                  padding: 2,
                },
                draggable: false,
                toolbar: false,
                toolbarButtonAlignment: "right",
                pageSize: 10,
                detailPanelType: "single",
                padding: 'dense',
                paging: false,
                filtering: false,
                searchFieldAlignment: "left",
              }}
              components={{
                ...defaultComponentsScroll,
                Body: (props) => (
                  <MaterialTableBody
                    {...props}
                    rowHeight={40}
                    overscanRowCount={10}
                    addVirtualScrolling={true}
                    bodyHeight={bodyHeight}
                    headerHeight={thead}
                    filterRowHeight={56}
                  />
                ),
              }}
              // actions={[
              //   (rowData) => (
              //     {
              //       icon: 'file_copy',
              //       tooltip: 'Duplicate',
              //       position: 'row',
              //       onClick: handleDuplicate,
              //       disabled: !state.canEdit,
              //       isFreeAction: false
              //     }
              //   )
              // ]}
              title="Menu Maintenance"
              columns={getColumns({canEdit: false})}
              data={rootMenuItems.data}
              editable={{
                isEditable: () => {
                  return state.canEdit;
                },
                isDeletable: () => {
                  return state.canEdit;
                },
                onRowAdd: state.canEdit
                    ? (newData) => new Promise((resolve, reject) => {
                      handleAddMenuItem.mutate({record: {...newData, tariffType: "COMPANY", endDate: "9999-12-31 00:00:00"}, resolve, reject, extraKeys});
                    })
                    : undefined,
                onRowUpdate: (newData, oldData) =>
                    new Promise((resolve, reject) => {
                      handleUpdateMenuItem.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
                    }),
                // onRowDelete: (oldData) =>
                //     new Promise((resolve, reject) => {
                //       handleDeleteTariffHeader.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                //     }),
              }}
            />
          </div>
        </div>
      </div>
  );

}

export default MenuRootList;
